import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import HistoryPage from '@/pages/HistoryPage';
import ReportsPage from '@/pages/ReportsPage';
import InvoicesPage from '@/pages/InvoicesPage';
import InvoiceFormPage from '@/pages/InvoiceFormPage';
import InvoiceDetailPage from '@/pages/InvoiceDetailPage';
import ClientFormPage from '@/pages/ClientFormPage';
import CompanySettingsPage from '@/pages/CompanySettingsPage';
import GoogleBusinessPage from '@/pages/GoogleBusinessPage';
import DepositReceiptPage from '@/pages/DepositReceiptPage';
import OrderAlertsPage from '@/pages/OrderAlertsPage';
import ClientNotesPage from '@/pages/ClientNotesPage';
import SettingsPage from '@/pages/SettingsPage';
import SAVPage from '@/pages/SAVPage';
import ReviewsPage from '@/pages/ReviewsPage';
import HRAgendaPage from '@/pages/HRAgendaPage';
import WebsiteQuotesPage from '@/pages/WebsiteQuotesPage';
import TechnicalDocFormPage from '@/pages/TechnicalDocFormPage';
import AccountingPage from '@/pages/AccountingPage';
import BilanDashboardPage from '@/pages/BilanDashboardPage';
import FraisProPage from '@/pages/FraisProPage';
import BankTrackingPage from '@/pages/BankTrackingPage';
import CashDepositsPage from '@/pages/CashDepositsPage';
import MonthlyChargesPage from '@/pages/MonthlyChargesPage';
import FinancialSummaryPage from '@/pages/FinancialSummaryPage';
import CashFlowPage from '@/pages/CashFlowPage';
import LoyaltyPage from '@/pages/LoyaltyPage';
import ProductCensusPage from '@/pages/ProductCensusPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import TermsOfServicePage from '@/pages/TermsOfServicePage';
import RemoteSignaturePage from '@/pages/RemoteSignaturePage';
import InvoiceWarrantySignPage from '@/pages/InvoiceWarrantySignPage';
import InvoiceWarrantyHistoryPage from '@/pages/InvoiceWarrantyHistoryPage';
import InvoiceWarrantyFormPage from '@/pages/InvoiceWarrantyFormPage';
import { Toaster } from '@/components/ui/sonner';

function App() {
  // Initialize auth state synchronously from localStorage to avoid redirect race condition
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    return !!(token && userData);
  });
  const [user, setUser] = useState(() => {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  });

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
  };

  return (
    <div className="App">
      <Toaster position="top-right" />
      <BrowserRouter>
        <Routes>
          {/* Route PUBLIQUE pour signature à distance (pas d'authentification requise) */}
          <Route path="/signature/:dossierId/:token" element={<RemoteSignaturePage />} />
          
          {/* Route PUBLIQUE pour signature garantie facture (pas d'authentification requise) */}
          <Route path="/invoice-warranty-sign/:invoiceId/:token" element={<InvoiceWarrantySignPage />} />
          
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <LoginPage onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <DashboardPage user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/history"
            element={
              isAuthenticated ? (
                <HistoryPage user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/reports"
            element={
              isAuthenticated ? (
                <ReportsPage user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/accounting"
            element={
              isAuthenticated ? (
                <AccountingPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/bilan-dashboard"
            element={
              isAuthenticated ? (
                <BilanDashboardPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/frais-pro"
            element={
              isAuthenticated ? (
                <FraisProPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/bank-tracking"
            element={
              isAuthenticated ? (
                <BankTrackingPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/cash-deposits"
            element={
              isAuthenticated ? (
                <CashDepositsPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/monthly-charges"
            element={
              isAuthenticated ? (
                <MonthlyChargesPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/financial-summary"
            element={
              isAuthenticated ? (
                <FinancialSummaryPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/invoices"
            element={
              isAuthenticated ? (
                <InvoicesPage user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/invoices/new"
            element={
              isAuthenticated ? (
                <InvoiceFormPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/invoices/edit/:id"
            element={
              isAuthenticated ? (
                <InvoiceFormPage user={user} editMode={true} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/invoices/:id"
            element={
              isAuthenticated ? (
                <InvoiceDetailPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/invoices/warranty/history"
            element={
              isAuthenticated ? (
                <InvoiceWarrantyHistoryPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/invoices/warranty/new"
            element={
              isAuthenticated ? (
                <InvoiceWarrantyFormPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/clients/new"
            element={
              isAuthenticated ? (
                <ClientFormPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/clients/:id"
            element={
              isAuthenticated ? (
                <ClientFormPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/settings/company"
            element={
              isAuthenticated ? (
                <CompanySettingsPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/google-business"
            element={
              isAuthenticated ? (
                <GoogleBusinessPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/deposit-receipts"
            element={
              isAuthenticated ? (
                <DepositReceiptPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/order-alerts"
            element={
              isAuthenticated ? (
                <OrderAlertsPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/client-notes"
            element={
              isAuthenticated ? (
                <ClientNotesPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/settings"
            element={
              isAuthenticated ? (
                <SettingsPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/sav"
            element={
              isAuthenticated ? (
                <SAVPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/reviews"
            element={
              isAuthenticated ? (
                <ReviewsPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/hr-agenda"
            element={
              isAuthenticated ? (
                <HRAgendaPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/website-quotes"
            element={
              isAuthenticated ? (
                <WebsiteQuotesPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/technical-docs/diagnostic"
            element={
              isAuthenticated ? (
                <TechnicalDocFormPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/technical-docs/diagnostic/:id"
            element={
              isAuthenticated ? (
                <TechnicalDocFormPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/technical-docs/irreparability"
            element={
              isAuthenticated ? (
                <TechnicalDocFormPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/technical-docs/irreparability/:id"
            element={
              isAuthenticated ? (
                <TechnicalDocFormPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route
            path="/cash-flow"
            element={
              isAuthenticated ? (
                <CashFlowPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/loyalty"
            element={
              isAuthenticated ? (
                <LoyaltyPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/product-census"
            element={
              isAuthenticated ? (
                <ProductCensusPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;